import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ToastNotification.css';

export interface ToastNotificationData {
  id: string;
  title: string;
  body?: string;
  icon?: string;
  onClick?: () => void;
  duration?: number; // ms, default 8000
}

interface ToastItemProps {
  toast: ToastNotificationData;
  onClose: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300); // Wait for animation
  }, [onClose, toast.id]);

  useEffect(() => {
    const duration = toast.duration || 8000;
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
  }, [handleClose, toast.duration]);

  const handleClick = () => {
    if (toast.onClick) {
      toast.onClick();
    }
    handleClose();
  };

  return (
    <div
      className={`toast-notification ${isExiting ? 'toast-notification-exit' : ''}`}
      onClick={handleClick}
    >
      <div className="toast-notification-content">
        {toast.icon && (
          <img src={toast.icon} alt="" className="toast-notification-icon" />
        )}
        <div className="toast-notification-text">
          <div className="toast-notification-title">{toast.title}</div>
          {toast.body && (
            <div className="toast-notification-body">{toast.body}</div>
          )}
        </div>
      </div>
      <button
        className="toast-notification-close"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Close notification"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Global toast state management
let toastListeners: ((toasts: ToastNotificationData[]) => void)[] = [];
let currentToasts: ToastNotificationData[] = [];

const notifyListeners = () => {
  toastListeners.forEach(listener => listener([...currentToasts]));
};

export const showToastNotification = (
  title: string,
  options: {
    body?: string;
    icon?: string;
    tag?: string;
    onClick?: () => void;
    duration?: number;
  } = {}
): void => {
  const id = options.tag || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Remove existing toast with same tag (if tag provided)
  if (options.tag) {
    currentToasts = currentToasts.filter(t => t.id !== id);
  }

  const toast: ToastNotificationData = {
    id,
    title,
    body: options.body,
    icon: options.icon || '/logo_icon_only.png',
    onClick: options.onClick,
    duration: options.duration,
  };

  currentToasts = [...currentToasts, toast];

  // Limit to max 5 toasts
  if (currentToasts.length > 5) {
    currentToasts = currentToasts.slice(-5);
  }

  notifyListeners();
  console.log('[ToastNotification] Showing in-page notification:', title);
};

export const removeToastNotification = (id: string): void => {
  currentToasts = currentToasts.filter(t => t.id !== id);
  notifyListeners();
};

// Container component that renders all toasts
export const ToastNotificationContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastNotificationData[]>([]);

  useEffect(() => {
    const listener = (newToasts: ToastNotificationData[]) => {
      setToasts(newToasts);
    };

    toastListeners.push(listener);
    // Initialize with current toasts
    setToasts([...currentToasts]);

    return () => {
      toastListeners = toastListeners.filter(l => l !== listener);
    };
  }, []);

  if (toasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="toast-notification-container">
      {toasts.map(toast => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={removeToastNotification}
        />
      ))}
    </div>,
    document.body
  );
};

export default ToastNotificationContainer;
